// Utils
import { useToggle } from '@hooks';
import { appendUrlParams } from '@utils/append-url-params';

// UI
import { Icon, IconNames } from '@components/icon';
import { PhoneNumberModal } from './heading-and-title-bar/phone-number-modal';
import { AnalyticsRef } from '@components/analytics-ref';

export enum SocialSectionType {
  Account = 'account',
  Listing = 'listing',
}

type Props = {
  contacts: {
    bookDirectWebsiteUrl?: string;
    facebookPageUrl?: string;
    instagramUrl?: string;
    youtubeUrl?: string;
    twitterUrl?: string;
    pinterestUrl?: string;
    marketingWebsiteUrl?: string;
    publicPhoneNumber?: string;
    publicEmail?: string;
    linkedinUrl?: string;
  };
  shouldShowSocialMedia?: boolean;
  shouldShowWebsiteUrlAsButton: string | boolean | undefined;
  type?: SocialSectionType;
};

export const SocialSection: React.FC<Props> = ({
  contacts,
  shouldShowSocialMedia = true,
  shouldShowWebsiteUrlAsButton,
  type = SocialSectionType.Listing,
}) => {
  const {
    on: phoneNumberModalIsOpen,
    setOff: closePhoneNumberModal,
    setOn: openPhoneNumberModal,
  } = useToggle();

  if (
    !contacts.facebookPageUrl &&
    !contacts.instagramUrl &&
    !contacts.youtubeUrl &&
    !contacts.marketingWebsiteUrl &&
    !contacts.publicPhoneNumber &&
    !contacts.publicEmail &&
    !contacts.pinterestUrl &&
    !contacts.linkedinUrl
  ) {
    return null;
  }

  return (
    <div>
      <div className="flex overflow-x-auto w-full justify-start items-center text-primary-main space-x-4">
        {!shouldShowWebsiteUrlAsButton && contacts.marketingWebsiteUrl && (
          <AnalyticsRef name={`select_${type}_website`}>
            <a
              href={appendUrlParams(contacts.marketingWebsiteUrl, {
                utm_source: 'Savvy',
                utm_medium: 'website',
              })}
              target="_blank"
              rel="noreferrer"
            >
              <Icon
                size="lg"
                name={IconNames.BROWSER}
                className="w-7 h-7 hover:opacity-80"
              />
            </a>
          </AnalyticsRef>
        )}

        {contacts.publicPhoneNumber && (
          <div>
            <PhoneNumberModal
              isOpen={phoneNumberModalIsOpen}
              closeModal={closePhoneNumberModal}
              phoneNumber={contacts.publicPhoneNumber}
            />
            <AnalyticsRef name={`select_${type}_phone`}>
              <button
                style={{ outline: 'none' }}
                onClick={openPhoneNumberModal}
              >
                <Icon
                  size="lg"
                  name={IconNames.PHONE}
                  className="w-7 h-7 hover:opacity-80"
                />
              </button>
            </AnalyticsRef>
          </div>
        )}
        {contacts.publicEmail && (
          <AnalyticsRef name={`select_${type}_email`}>
            <a
              href={`mailto:${contacts.publicEmail}?subject=Inquiry from Savvy`}
            >
              <Icon
                size="lg"
                name={IconNames.ENVELOPE}
                className="w-7 h-7 hover:opacity-80"
              />
            </a>
          </AnalyticsRef>
        )}

        {contacts.facebookPageUrl && shouldShowSocialMedia && (
          <AnalyticsRef name={`select_${type}_social`}>
            <a
              href={contacts.facebookPageUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Icon
                size="lg"
                name={IconNames.FACEBOOK}
                className="w-7 h-7 hover:opacity-80"
              />
            </a>
          </AnalyticsRef>
        )}
        {contacts.instagramUrl && shouldShowSocialMedia && (
          <AnalyticsRef name={`select_${type}_social`}>
            <a
              href={contacts.instagramUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Icon
                size="lg"
                name={IconNames.INSTAGRAM}
                className="w-7 h-7 hover:opacity-80"
              />
            </a>
          </AnalyticsRef>
        )}
        {contacts.youtubeUrl && shouldShowSocialMedia && (
          <AnalyticsRef name={`select_${type}_social`}>
            <a
              href={contacts.youtubeUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Icon
                size="lg"
                name={IconNames.YOUTUBE}
                className="w-7 h-7 hover:opacity-80"
              />
            </a>
          </AnalyticsRef>
        )}
        {contacts.twitterUrl && shouldShowSocialMedia && (
          <AnalyticsRef name={`select_${type}_social`}>
            <a
              href={contacts.twitterUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Icon
                size="lg"
                name={IconNames.TWITTERX}
                className="w-7 h-7 hover:opacity-80"
              />
            </a>
          </AnalyticsRef>
        )}
        {contacts.pinterestUrl && shouldShowSocialMedia && (
          <AnalyticsRef name={`select_${type}_social`}>
            <a
              href={contacts.pinterestUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Icon
                size="lg"
                name={IconNames.PINTEREST}
                className="w-7 h-7 hover:opacity-80"
              />
            </a>
          </AnalyticsRef>
        )}
        {contacts.linkedinUrl && shouldShowSocialMedia && (
          <AnalyticsRef name={`select_${type}_social`}>
            <a
              href={contacts.linkedinUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Icon
                size="lg"
                name={IconNames.LINKEDIN}
                className="w-7 h-7 hover:opacity-80"
              />
            </a>
          </AnalyticsRef>
        )}
      </div>
    </div>
  );
};
